import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import { Button, ErrorBanner, Input, Modal, Spinner } from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toE164 } from "@/lib/format";
import { useCustomerCrud } from "@/lib/queries";
import type { Customer, Me } from "@/lib/types";

/** Cadastro de cliente, usado na tela de Clientes e no cadastro de veículo.
 *
 * Vive num componente próprio porque são dois pontos de entrada para a mesma
 * coisa: quem abre "Clientes" para cadastrar, e quem está cadastrando um
 * carro e descobre que o dono ainda não existe. Duplicar o formulário faria
 * as duas telas divergirem na primeira regra nova.
 *
 * `onCreated` devolve o cliente gravado — é o que permite ao formulário de
 * veículo já selecioná-lo sem o usuário procurar de novo na lista.
 */
export default function CustomerFormModal({
  open,
  onClose,
  onCreated,
  customer,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (c: Customer) => void;
  customer?: Customer | null;
}) {
  const t = useT();
  const { create, update } = useCustomerCrud();
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [aberto, setAberto] = useState(false);

  // Recarrega quando o modal abre, para não mostrar o rascunho anterior.
  if (open && !aberto) {
    setAberto(true);
    setForm({
      name: customer?.name ?? "",
      phone: customer?.phone ?? "",
      email: customer?.email ?? "",
      notes: customer?.notes ?? "",
    });
  }
  if (!open && aberto) setAberto(false);

  const mutation = customer ? update : create;
  const erro = mutation.isError ? toApiError(mutation.error) : null;

  // O telefone é a chave do cliente na oficina (uniq_customer_phone_per_garage)
  // e o que o backend valida; 10 dígitos é o fixo com DDD.
  const { region } = useOutletContext<Me>().garage;
  const digitos = form.phone.replace(/\D/g, "").length;
  const podeSalvar = form.name.trim().length >= 2 && digitos >= 10;

  const salvar = () => {
    const body = {
      ...form,
      // O país da oficina decide o prefixo. Sem ele, o telefone do
      // cliente de Miami era gravado como +55 — e isso não é tela feia,
      // é dado errado no banco, que depois precisa de migração.
      phone: toE164(form.phone, region.country),
      ...(customer ? { id: customer.id } : {}),
    };
    mutation.mutate(body as never, {
      onSuccess: (criado) => {
        onCreated?.(criado as Customer);
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={customer ? t("modal.editCustomer") : t("modal.newCustomer")}
    >
      <div className="space-y-4">
        <ErrorBanner message={erro?.message} code={erro?.code} />

        <Input
          label={t("common.name")}
          value={form.name}
          placeholder={t("modal.fullNamePlaceholder")}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label={region.messaging_label}
          type="tel"
          inputMode="tel"
          placeholder={region.country === "US" ? "(305) 555-0199" : "(11) 99999-9999"}
          hint={t("modal.phoneHint")}
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Input
          label={`${t("common.email")} (${t("common.optional")})`}
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label={t("modal.notesOptional")}
          value={form.notes}
          placeholder={t("modal.notesPlaceholder")}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="racing" disabled={!podeSalvar || mutation.isPending} onClick={salvar}>
            {mutation.isPending ? <Spinner className="size-4" /> : t("common.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
